import React, { useCallback, useMemo } from 'react';
import { debounce } from 'lodash-es';
import cx from 'classnames';
import { RichInputVariant } from '@mutiny-pkg/champaign-schemas/src/schemas/richText';
import { RichText } from '@mutiny-pkg/champaign-schemas/src/schemas/richText/interfaces';
import {
  ButtonTreeNodeData,
  StreamingButtonTreeNodeData,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { MentionItem } from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionsPlugin';
import { hydrateRichTextVariables, renderRichTextToPlainString, normalizeStreamingRichText } from '../../richtext/ast';
import { renderRichTextToHtml } from '../../richtext/ssr';
import { LexicalThemeColors } from '../../richtext/types';
import { RichInput } from '@mutiny-pkg/bin-ui/src/richtext/RichInput/RichInput';
import { transformToContainerQueries } from '../utils/transformBreakpointClasses';

type ButtonProps = StreamingButtonTreeNodeData & {
  id: string;
  editorClassName?: string;
  onChange?: (id: string, data: Partial<ButtonTreeNodeData>) => void;
  mentionItems: MentionItem[];
  refetchMentionitems: () => Promise<void>;
  overrideVariables?: Partial<Record<string, string>>;
  isEditor: boolean;
  onBlur?: () => void;
  onDoubleClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  isDoubleClickActive?: boolean;
  themeColors?: LexicalThemeColors;
};

type ButtonElementProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  commandfor?: string;
  command?: string;
  ['data-triggers-dialog']?: string;
};

export const Button = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      editorClassName,
      text,
      variant,
      id,
      onChange,
      mentionItems,
      refetchMentionitems,
      overrideVariables,
      href,
      isEditor,
      onBlur,
      onDoubleClick,
      isDoubleClickActive,
      themeColors,
      commandfor,
      command,
      ...props
    },
    ref,
  ) => {
    const isEditing = isDoubleClickActive;

    // Normalize streaming RichText to complete RichText for safe processing
    const normalizedText = useMemo(() => normalizeStreamingRichText(text), [text]);
    const normalizedHref = useMemo(() => (href ? normalizeStreamingRichText(href) : undefined), [href]);

    // Generate variant class from data.variant, or check if it's already in className
    const variantClassName = useMemo(() => {
      if (variant) {
        return `btn-${variant}`;
      }
      // Check if className already has a button variant class (backward compatibility)
      if (typeof className === 'string' && className.match(/btn-(primary|secondary|tertiary|outline|ghost|link)/)) {
        return undefined; // variant already in className, don't duplicate
      }
      return undefined;
    }, [variant, className]);

    // Transform className for container queries (editor preview)
    const combinedClassName = cx(
      'w-fit inline-block',
      transformToContainerQueries(className, { isEditor }),
      variantClassName,
    );

    const hydratedText = useMemo(() => {
      return typeof normalizedText === 'string'
        ? normalizedText
        : hydrateRichTextVariables(normalizedText, overrideVariables || {}, isEditor);
    }, [normalizedText, overrideVariables, isEditor]);

    const hydratedHref = useMemo(() => {
      if (!normalizedHref) return undefined;
      return typeof normalizedHref === 'string'
        ? normalizedHref
        : hydrateRichTextVariables(normalizedHref, overrideVariables || {}, isEditor);
    }, [normalizedHref, overrideVariables, isEditor]);

    const renderedHtml = useMemo(() => {
      return renderRichTextToHtml(hydratedText, undefined, { isEditor, mentionItems });
    }, [hydratedText, isEditor, mentionItems]);

    const handleDoubleClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        onDoubleClick?.(event);
      },
      [onDoubleClick],
    );

    const handleBlur = useCallback(() => {
      onBlur?.();
    }, [onBlur]);

    const debouncedOnChange = useMemo(
      () =>
        debounce((newValue: RichText) => {
          onChange?.(id, { text: newValue });
        }, 300),
      [onChange, id],
    );

    const handleChange = useCallback(
      (newValue: RichText) => {
        debouncedOnChange(newValue);
      },
      [debouncedOnChange],
    );

    const content = isEditing ? (
      <span data-lexical-editor onDoubleClick={handleDoubleClick} style={{ cursor: 'text' }}>
        <RichInput
          value={normalizedText}
          onChange={handleChange}
          onBlur={handleBlur}
          editable
          enableTextAlign={false}
          enableFloatingMenu={false}
          enableExtendedTextFormatting
          variant={RichInputVariant.InlineEditing}
          autoFocus
          mentionItems={mentionItems}
          onMentionMenuOpen={refetchMentionitems}
          themeColors={themeColors}
        />
      </span>
    ) : (
      <span data-lexical-editor onDoubleClick={handleDoubleClick} dangerouslySetInnerHTML={{ __html: renderedHtml }} />
    );

    if (!href || (command && commandfor)) {
      const buttonProps: ButtonElementProps = {
        ...props,
        command,
        commandfor,
      };

      if (command === 'show-modal') {
        buttonProps['data-triggers-dialog'] = commandfor;
      }

      return (
        <button
          id={id}
          type="button"
          ref={ref as React.Ref<HTMLButtonElement>}
          className={cx(combinedClassName, editorClassName)}
          data-no-dnd={isEditing}
          style={{ cursor: isEditing ? 'text' : undefined }}
          {...buttonProps}
        >
          {content}
        </button>
      );
    }

    const { openInNewTab, ...restProps } = props;

    const anchorProps = {
      ...restProps,
      href: hydratedHref ? renderRichTextToPlainString(hydratedHref) : undefined,
      target: openInNewTab ? '_blank' : '_self',
    };

    return (
      <a
        id={id}
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={cx(combinedClassName, editorClassName)}
        data-no-dnd={isEditing}
        style={{ cursor: isEditing ? 'text' : undefined }}
        rel="noreferrer"
        {...anchorProps}
      >
        {content}
      </a>
    );
  },
);

Button.displayName = 'Button';
